import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["termPaymentDetails", "id", "selectedLender", "createdTime", "modifiedTime", "paymentOption"],
    _excluded2 = ["lender", "lenderId", "selected"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { defaultMemoize } from 'reselect';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _set from 'lodash/set';
import _keyBy from 'lodash/keyBy';
import _setWith from 'lodash/setWith';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _compact from 'lodash/compact';
import _values from 'lodash/values';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _remove from 'lodash/remove';
import _reduce from 'lodash/reduce';
import _pick from 'lodash/pick';
import _property from 'lodash/property';
import _map from 'lodash/map';
import _flatten from 'lodash/flatten';
import { EMPTY_OBJECT, EMPTY_ARRAY, EMPTY_STRING, NO_DATA } from '../../app.constants';
import Money from '../../utils/money';
import { getVehicleAgeInDays } from '../../helpers/vehicle.helper';
import { DEAL_USER_TYPE } from '../../constants/deal/userType';
import { DEAL_STATUS } from '../../constants/deal/status';
import { getFormattedDateTime, getMomentValueOf, toMoment, getTimeStamp, getDifferenceAsDays, DATE_TIME_FORMATS } from '../../utils/dateUtils';
import { CATEGORY_1_DEAL_TYPES } from '../constants/startDeal.constants';
import { formatCurrencyWithMoneyFormat, getDaysFromDuration, getNumber, getRandomId, isCreatedBySystem } from '../utils';
import { getRetailPrice, isTempVehicle, getTrimValuesWithSpaceSeparator } from './vehicle.reader';
import { isTempCustomer, getCurrentEmploymentInfoOfBuyer } from './customer.reader';
import * as ZipCodeTaxReader from './zipCodeTax.reader';
import { getAssigneeInfo } from './dealSummary.reader';
import { CUSTOMER_TYPE, DEAL_STATUS_WEIGHTS } from '../constants/deal.constants';
import { GROSS_TYPES, DOWN_PAYMENT_TYPES, TAX_AND_ZIP_CODE_DETAILS_VS_KEY } from '../constants/desking.constants';
import { RECEIVABLES_TYPES } from '../constants/contractTable.constants';
import { isLeseOrOPLeasePaymentType, isLoanOrCashOrBalloonPaymentType } from '../helpers/desking.helpers'; // import * as RoleReader from 'utils/role.reader';
// import * as ColumnDataReader from 'pages/desking/components/dealDetails/columnData.reader';
// import { getSelectedColumnData } from '../pages/desking/components/dealDetails/columnData.reader';

export function getPrimaryVehicle(deal) {
  var vehicles = _get(deal, 'vehicles') || EMPTY_ARRAY;
  return vehicles.find(function (vehicle) {
    return vehicle.primaryVehicle;
  });
}
export function getVehicleBasedOnId(deal, vehicleId) {
  var vehicles = _get(deal, 'vehicles') || EMPTY_ARRAY;
  return vehicles.find(function (vehicle) {
    return (vehicle === null || vehicle === void 0 ? void 0 : vehicle.dealVehicleId) === vehicleId;
  });
}
export function getBuyer(deal) {
  var customersIndeal = _get(deal, 'customers') || EMPTY_ARRAY;
  return customersIndeal.find(function (_ref) {
    var type = _ref.type;
    return type === CUSTOMER_TYPE.BUYER;
  }) || EMPTY_OBJECT;
}
export function getCoBuyer(deal) {
  var customersIndeal = _get(deal, 'customers') || EMPTY_ARRAY;
  return customersIndeal.find(function (_ref2) {
    var type = _ref2.type;
    return type === CUSTOMER_TYPE.CO_BUYER;
  });
}
export function isBuyerAndCustomerIdPresent(deal) {
  var buyer = getBuyer(deal);
  return {
    isBuyer: !_isEmpty(buyer),
    isBuyerCustomerId: !!_get(buyer, 'customerId', false) || false
  };
}
export function getCreditScoreOfBuyer(deal) {
  var buyer = getBuyer(deal);
  return _get(buyer, 'creditScore') || 0;
}
/**
 *
 * If Primary vehicle does not contain id, it returns the same object.
 * If it contains id, it picks the data for the id from allVehicles and returns.
 */

export function getPrimaryVehicleInfo(deal, allVehicles) {
  var primaryVehicle = getPrimaryVehicle(deal);

  if (primaryVehicle && primaryVehicle.id) {
    return _get(allVehicles, [primaryVehicle.id]);
  }

  return primaryVehicle;
}
export var getPrimaryVehicleId = function getPrimaryVehicleId(deal) {
  var primaryVehicle = getPrimaryVehicle(deal) || EMPTY_OBJECT;
  return primaryVehicle.id;
};
export function getNonPrimaryVehicles(deal) {
  var _deal$vehicles = deal.vehicles,
      vehicles = _deal$vehicles === void 0 ? [] : _deal$vehicles;
  return vehicles.filter(function (vehicle) {
    return !vehicle.primaryVehicle;
  });
}
export function getPrimaryVehicleStockNumber(deal) {
  var primaryVehicle = getPrimaryVehicle(deal) || EMPTY_OBJECT;
  return primaryVehicle.stockID;
}
export function getPrimaryVehicleSource(deal) {
  var primaryVehicle = getPrimaryVehicle(deal) || EMPTY_OBJECT;
  var source = _get(primaryVehicle, ['sourceInfo', 'source']) || EMPTY_STRING;
  return source;
}
export function getPrimaryVehicleVIN(deal) {
  var primaryVehicle = getPrimaryVehicle(deal) || EMPTY_OBJECT;
  return primaryVehicle.vin;
}
export function getSoldAtSiteId(deal) {
  return _get(deal, 'soldAtSiteId');
}
export function getPrimaryVehicleMake(deal) {
  var primaryVehicle = getPrimaryVehicle(deal) || EMPTY_OBJECT;
  return primaryVehicle.make;
}
export function getSelectedVehiclePrice(deal, allVehiclesData) {
  var withFormat = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  var selectedVehicle = getPrimaryVehicle(deal) || EMPTY_OBJECT;
  var price = selectedVehicle.id ? getRetailPrice(_get(allVehiclesData, selectedVehicle.id)) : getRetailPrice(selectedVehicle);
  if (!withFormat) return price;
  return price ? formatCurrencyWithMoneyFormat(price) : '';
}
export function getSelectedVehicleType(deal, allVehiclesData) {
  var selectedVehicle = getPrimaryVehicle(deal) || EMPTY_OBJECT;

  if (selectedVehicle.id) {
    return _get(allVehiclesData, [selectedVehicle.id, 'vehicleType']);
  }

  return _get(selectedVehicle, ['vehicleType']);
}
export function getCustomerIds() {
  var deal = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  return {
    buyerId: deal.buyerId,
    coBuyerId: deal.coBuyerId
  };
}
export function getAssigneesIdsOfType(deal, type) {
  var assignees = deal.assignees || [];
  var filteredAssignees = assignees.filter(function (assignee) {
    return assignee.type === type;
  });
  return filteredAssignees.map(function (assignee) {
    return assignee.assigneeId;
  });
}
export function getAssignees(deal) {
  return deal.assignees || [];
}
export function getCommissionList(userIds) {
  var lookupUserInfo = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var commissions = [];
  userIds.forEach(function (usr) {
    var userId = usr.userId,
        id = usr.id;
    var userInfo = lookupUserInfo[userId] || {};

    var _getAssigneeInfo = getAssigneeInfo(userInfo),
        displayName = _getAssigneeInfo.displayName,
        profilePicture = _getAssigneeInfo.profilePicture,
        fname = _getAssigneeInfo.fname,
        lname = _getAssigneeInfo.lname;

    commissions.push(_objectSpread(_objectSpread({}, usr), {}, {
      displayName: displayName,
      fname: fname,
      lname: lname,
      profilePicture: profilePicture,
      id: id || getRandomId()
    }));
  }); // Making the PrimarySalesPerson on top and empty assinees at the end

  var salesPersons = _remove(commissions, function (assignee) {
    return assignee.userType === DEAL_USER_TYPE.SALES_PERSON;
  }) || [];
  var emptyAssignees = _remove(commissions, function (assignee) {
    return !assignee.userId;
  }) || [];
  salesPersons = salesPersons.sort(function (a, b) {
    return b.primarySalesPerson - a.primarySalesPerson;
  });
  commissions = [].concat(_toConsumableArray(commissions), _toConsumableArray(salesPersons), _toConsumableArray(emptyAssignees));
  return commissions;
}
export var getContractDateForDisplay = function getContractDateForDisplay(deal) {
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DATE_TIME_FORMATS.ABBREVIATED_DATE_ALPHA_MONTH;

  var contractDate = _get(deal, 'soldTime');

  return contractDate ? getFormattedDateTime(contractDate, format) : NO_DATA;
};
export function getDealCreatedTimeForDisplay(deal) {
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DATE_TIME_FORMATS.ABBREVIATED_DATE_ALPHA_MONTH;
  var createdTime = deal.createdTime;
  return getFormattedDateTime(createdTime, format);
}
export function getDealBookedDateForDisplay(deal) {
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DATE_TIME_FORMATS.ABBREVIATED_DATE_ALPHA_MONTH;

  if (!(deal !== null && deal !== void 0 && deal.bookedDate)) {
    return NO_DATA;
  }

  return getFormattedDateTime(deal === null || deal === void 0 ? void 0 : deal.bookedDate, format);
}
export function getDealModifiedTimeForDisplay(deal) {
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DATE_TIME_FORMATS.ABBREVIATED_DATE_ALPHA_MONTH;
  var modifiedTime = deal.modifiedTime;
  return getFormattedDateTime(modifiedTime, format);
}
export function getAllAssigneesExceptOfType(deal, assigneeTypeToExclude) {
  var assignees = deal.assignees || [];
  return assignees.filter(function (assignee) {
    return assignee.type !== assigneeTypeToExclude;
  });
}
export function getPrimaryVehicleDealVehicleId(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, 'dealVehicleId');
}
export function getDealPaymentsForDealVehicleId(deal, targetDealVehicleId) {
  var dealVehiclePaymentInfoForAllVehicles = _get(deal, 'dealVehiclePaymentInfos') || [];
  var dealVehiclePaymentInfo = dealVehiclePaymentInfoForAllVehicles.find(function (_ref3) {
    var dealVehicleId = _ref3.dealVehicleId;
    return dealVehicleId === targetDealVehicleId;
  });
  return _get(dealVehiclePaymentInfo, 'dealPayments') || [];
}
export function getDealPaymentsForSelectedVehicle(deal, dealVehicleId) {
  var vehicleIdToUse = dealVehicleId;

  if (!vehicleIdToUse) {
    vehicleIdToUse = getPrimaryVehicleDealVehicleId(deal);
  }

  var dealVehiclePaymentInfoForAllVehicles = _get(deal, 'dealVehiclePaymentInfos') || [];
  var dealVehiclePaymentInfo = dealVehiclePaymentInfoForAllVehicles.find(function (_ref4) {
    var id = _ref4.dealVehicleId;
    return id === vehicleIdToUse;
  });
  return _map(_get(dealVehiclePaymentInfo, 'dealPayments'), function (dealPayments) {
    return _objectSpread(_objectSpread({}, dealPayments), {}, {
      dealVehicleId: vehicleIdToUse
    });
  });
}
export function getDealPaymentsForAllVehicles(deal) {
  var dealVehiclePaymentInfoForAllVehicles = _get(deal, 'dealVehiclePaymentInfos') || [];
  return _flatten(_map(dealVehiclePaymentInfoForAllVehicles, function (_ref5) {
    var dealVehicleId = _ref5.dealVehicleId,
        dealPayments = _ref5.dealPayments;

    var allDealPaymentForvehicle = _map(dealPayments, function (dealPayment) {
      return _objectSpread(_objectSpread({}, dealPayment), {}, {
        dealVehicleId: dealVehicleId
      });
    });

    return allDealPaymentForvehicle;
  }));
}
export function getDownPaymentsFromDeskingPaymentDetails() {
  var deal = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var deskingPaymentDetails = getDealPaymentsForSelectedVehicle(deal);
  var isMultipleDownPaymentEnabled = isMultipleDownPaymentsEnabled(deal);
  var dealType = getDealType(deal);
  var loanDownPayments = [];
  var leaseDownPayments = [];
  var MAX_DOWNPAYMENTS = CATEGORY_1_DEAL_TYPES.includes(dealType) ? 3 : 1;

  if (!isMultipleDownPaymentEnabled) {
    var termPaymentDetails = _get(deskingPaymentDetails, [0, 'termPaymentDetails']) || EMPTY_ARRAY;
    termPaymentDetails.forEach(function (_ref6) {
      var downPayment = _ref6.downPayment;
      leaseDownPayments.push(downPayment);
      loanDownPayments.push(downPayment);
    });
  } else {
    deskingPaymentDetails.forEach(function (singleColumnInfo) {
      var paymentType = _get(singleColumnInfo, ['paymentOption', 'paymentType']);

      var termPaymentDetails = _get(singleColumnInfo, 'termPaymentDetails') || [];
      if (isLeseOrOPLeasePaymentType(paymentType) && !_isEmpty(leaseDownPayments)) return;
      if (isLoanOrCashOrBalloonPaymentType(paymentType) && !_isEmpty(loanDownPayments)) return;
      termPaymentDetails.forEach(function (termPaymentDetail) {
        var downPayment = _get(termPaymentDetail, 'downPayment');

        if (isLeseOrOPLeasePaymentType(paymentType)) {
          leaseDownPayments.push(downPayment);
        } else {
          loanDownPayments.push(downPayment);
        }
      });
    });
  }

  var downPayments = [];

  var _loop = function _loop(i) {
    var emptyDownPayment = {};

    _values(DOWN_PAYMENT_TYPES).forEach(function (type) {
      if (type === DOWN_PAYMENT_TYPES.LOAN_DOWNPAYMENTS) {
        emptyDownPayment[type] = loanDownPayments[i] || 0;
      } else {
        emptyDownPayment[type] = leaseDownPayments[i] || 0;
      }
    });

    downPayments.push(emptyDownPayment);
  };

  for (var i = 0; i < MAX_DOWNPAYMENTS; i += 1) {
    _loop(i);
  }

  return downPayments;
}
export function getColumnsDataFromDeskingPaymentDetails(deskingPaymentDetails) {
  return deskingPaymentDetails.map(function (_ref7) {
    var termPaymentDetails = _ref7.termPaymentDetails,
        id = _ref7.id,
        selectedLender = _ref7.selectedLender,
        createdTime = _ref7.createdTime,
        modifiedTime = _ref7.modifiedTime,
        paymentOption = _ref7.paymentOption,
        restProps = _objectWithoutProperties(_ref7, _excluded);

    var selectedInfo = null;
    var termPaymentDetailsMapped = {};
    termPaymentDetails.forEach(function (termPaymentDetailFromServer, downpaymentId) {
      var termPaymentDetail = _omit(termPaymentDetailFromServer, ['modifiedTime', 'createdTime']);

      var lender = termPaymentDetail.lender,
          lenderId = termPaymentDetail.lenderId,
          selected = termPaymentDetail.selected,
          allOtherTermPaymentInfo = _objectWithoutProperties(termPaymentDetail, _excluded2);

      if (!selectedInfo || selected) {
        selectedInfo = {
          selectedDownPaymentId: downpaymentId,
          selectedDownPayment: termPaymentDetail.downPayment,
          selected: selected || false
        };
      }

      _setWith(termPaymentDetailsMapped, [lenderId, String(downpaymentId)], allOtherTermPaymentInfo, Object);
    });

    var formattedData = _objectSpread(_objectSpread(_objectSpread({
      paymentOption: paymentOption
    }, restProps), {}, {
      id: id
    }, selectedInfo), {}, {
      termPaymentDetails: termPaymentDetailsMapped
    });

    return formattedData;
  });
}
export function isMultipleDownPaymentsEnabled(deal) {
  return !!_get(deal, 'multipleDownPaymentsEnabled');
}
export function getDealStatus(deal) {
  return _get(deal, 'status');
}
export function getDefaultPaymentOptions(deal) {
  return _get(deal, ['paymentTemplate', 'paymentOptions']) || EMPTY_ARRAY;
}
export function getDealWithoutPaymentInfo(deal) {
  return _omit(deal, ['dealVehiclePaymentInfos']);
}
export function getDealWithoutDepositDetails(deal) {
  return _omit(deal, ['depositDetails']);
}
export function getDealWithoutDepositAmount(deal) {
  return _set(deal, ['depositDetails', 'deposit'], null);
}
export function setMarketScanIdForVehicle(deal, dealVehicleId, marketScanVehicleId, grossWeight, unladenWeight, styleId) {
  var vehicles = _get(deal, 'vehicles') || EMPTY_ARRAY;
  var matchingIndex = vehicles.findIndex(function (_ref8) {
    var dealVehicleIdInDeal = _ref8.dealVehicleId;
    return dealVehicleIdInDeal === dealVehicleId;
  });

  if (matchingIndex >= 0) {
    _setWith(deal, ['vehicles', matchingIndex, "marketScanVehicleId"], marketScanVehicleId, Object);

    _setWith(deal, ['vehicles', matchingIndex, "styleId"], styleId, Object);

    if (grossWeight) _setWith(deal, ['vehicles', matchingIndex, 'grossWeight'], grossWeight, Object);
    if (unladenWeight) _setWith(deal, ['vehicles', matchingIndex, 'unladenWeight'], unladenWeight, Object);
  }
}
export var getMergedDataOfPrimaryVehicle = defaultMemoize(function (deal, allVehicles) {
  var primaryVehicle = getPrimaryVehicle(deal);

  var vehicleList = _keyBy(_values(allVehicles), 'vin');

  if (_has(primaryVehicle, 'vin')) {
    var fullVehicleInfo = vehicleList[primaryVehicle.vin] || EMPTY_OBJECT;
    var pricingDetailInVehicleInfo = _get(fullVehicleInfo, 'pricingDetails') || EMPTY_OBJECT;
    var pricingDetailsInDeal = primaryVehicle.pricingDetails;
    /**
     * MERGING pricingDetailInVehicleInfo with pricingDetailsInDeal because
     * pricingDetailInVehicleInfo contains few keys like holdback amount which will not be there
     * in pricingDetailsInDeal.
     *
     * user can choose build vehicle for existing vin.
     */

    return _objectSpread(_objectSpread(_objectSpread({}, fullVehicleInfo), primaryVehicle), {}, {
      pricingDetails: _objectSpread(_objectSpread({}, pricingDetailInVehicleInfo), pricingDetailsInDeal)
    });
  }

  return primaryVehicle;
});
export var getTotalAdjustmentCost = function getTotalAdjustmentCost(costAdjustments) {
  return costAdjustments.reduce(function (total, item) {
    return Money.add(total, getNumber(item.amount));
  }, 0);
};
/**
 * 1. If vehicle present in deal contains id, then it wont contain much info about vehicle.
 *    hence merging it with vehicle info.
 * 2. If vehicle present in deal doesnot contain id, it means it is a custom built vehicle and it
 *    should contain all info.
 */

export var getPricingDataOfPrimaryVehicle = function getPricingDataOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  var pricingDetailsInDealVehicle = _get(primaryVehicle, 'pricingDetails') || EMPTY_OBJECT;
  return pricingDetailsInDealVehicle;
};
export var getMSpayloadForTradeInFromDeal = function getMSpayloadForTradeInFromDeal(tradeIns) {
  var tradeAllowance = 0;
  var tradePayOff = 0;
  var actualCashValue = 0; // eslint-disable-next-line array-callback-return

  tradeIns.map(function (item) {
    tradeAllowance = Money.add(tradeAllowance, getNumber(item.tradeAllowance));
    tradePayOff = Money.add(tradePayOff, Money.add(getNumber(item.tradePayOff), getNumber(item.retainedEquity)));
    actualCashValue = Money.add(actualCashValue, getNumber(item.actualCashValue));
  });
  return {
    ActualCashValueLeased: actualCashValue,
    AllowanceLeased: tradeAllowance,
    PayOffLeased: tradePayOff,
    ActualCashValuePurchased: actualCashValue,
    AllowancePurchased: tradeAllowance,
    PayOffPurchased: tradePayOff
  };
};
export function getDealType(deal) {
  return _get(deal, 'type') || '';
}
export function getFniSurveyStatus(deal) {
  return _get(deal, 'fniSurveyStatus');
}
export var getFormCustomer = function getFormCustomer(deal) {
  return _get(deal, 'formCustomerSelection') || '';
};
export var getFormTitle = function getFormTitle(deal) {
  return _get(deal, 'formCustomerTitleSelection') || '';
};
export var getContractDate = function getContractDate(deal) {
  var contractDate = _get(deal, 'soldTime');

  return contractDate ? toMoment(contractDate) : '';
};
export var getSoldDate = function getSoldDate(deal) {
  var bookedDate = _get(deal, 'bookedDate');

  return bookedDate ? toMoment(bookedDate) : '';
};
export var getDeliveryDate = function getDeliveryDate(deal) {
  return _get(deal, 'promisedDeliveryDate', '');
};
export var getReservedDate = function getReservedDate(deal) {
  var reservedDate = _get(deal, ['depositDetails', 'reservedDate']);

  return reservedDate ? toMoment(reservedDate) : '';
};
export var getPreCloseDate = function getPreCloseDate(deal) {
  var preCloseDate = _get(deal, 'preCloseAccountingDate');

  return preCloseDate ? toMoment(preCloseDate) : '';
};
export var getFinalAccountingDate = function getFinalAccountingDate(deal) {
  var closedDate = _get(deal, 'finalAccountingDate');

  return closedDate ? toMoment(closedDate) : '';
};
export var getDealDocumentsData = function getDealDocumentsData(category, documents) {
  if (!_isEmpty(documents)) {
    return documents.filter(function (document) {
      var _document$attributes;

      return (document.category || ((_document$attributes = document.attributes) === null || _document$attributes === void 0 ? void 0 : _document$attributes.formCategory)) === category;
    });
  }

  return EMPTY_ARRAY;
};
export function getDownPayment() {
  var deal = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var dealVehiclePaymentInfos = deal.dealVehiclePaymentInfos;
  var dealPayment = _get(dealVehiclePaymentInfos, '[0].dealPayments[0]') || {};
  return _get(dealPayment, 'termPaymentDetails[0].downPayment') || '';
}
export var getQuoteAge = function getQuoteAge(_ref9) {
  var quoteAge = _ref9.quoteAge;

  var _pick2 = _pick(quoteAge, ['startTime', 'endTime']),
      startTime = _pick2.startTime,
      endTime = _pick2.endTime;

  if (startTime && endTime) {
    return "".concat(getDifferenceAsDays(toMoment(endTime), toMoment(startTime)), " days");
  }

  return '';
};
export function getContractAge(_ref10) {
  var soldTime = _ref10.soldTime;
  var currentTime = getTimeStamp();
  if (!soldTime || soldTime > currentTime) return 0; // return humanize(currentTime - soldTime, 'milliseconds');

  return getDaysFromDuration(currentTime - getMomentValueOf(toMoment(soldTime).startOf('day')));
}
export var getDealNumber = function getDealNumber(deal) {
  return _get(deal, 'dealNumber');
};
export var getParentId = function getParentId(deal) {
  return _get(deal, 'parentId');
};
export var isDealDuplicated = function isDealDuplicated(deal) {
  return _get(deal, 'hasDuplicate');
};
export var getDealId = function getDealId(deal) {
  return !_isEmpty(deal) && _get(deal, 'id');
};
export var getCustomers = function getCustomers(deal) {
  return _get(deal, 'customers', []);
};
export var getTaxAndZipCodeDetails = function getTaxAndZipCodeDetails(deal, type) {
  return _get(deal, ['taxAndZipCodeDetails', type]) || EMPTY_OBJECT;
};
export var getTaxCode = function getTaxCode(deal, type) {
  return _get(getTaxAndZipCodeDetails(deal, type), 'taxCode') || EMPTY_STRING;
};
export function getMarketId(deal, type) {
  var taxAndZipCodeDetails = getTaxAndZipCodeDetails(deal, type);
  return _get(taxAndZipCodeDetails, 'marketId');
}
export function getZipCode(deal, type) {
  var taxAndZipCodeDetails = getTaxAndZipCodeDetails(deal, type);
  return _get(taxAndZipCodeDetails, 'zipCode');
}
export var getExistingTempCustomer = function getExistingTempCustomer(deal) {
  var customers = _get(deal, 'customers') || EMPTY_ARRAY;
  if (deal && isTempCustomer(customers)) return customers.find(function (customer) {
    return customer.temp;
  }) || EMPTY_OBJECT;
  return EMPTY_OBJECT;
};
export var getExistingTempVehicle = function getExistingTempVehicle(deal) {
  var vehicles = _get(deal, 'vehicles') || EMPTY_ARRAY;
  if (deal && isTempVehicle(vehicles)) return vehicles.find(function (vehicle) {
    return vehicle.temp;
  }) || EMPTY_OBJECT;
  return EMPTY_OBJECT;
};
export var getInvoicePriceOfPrimaryVehicle = function getInvoicePriceOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, ['pricingDetails', 'invoicePrice']);
};
export var getGLBalanceOfPrimaryVehicle = function getGLBalanceOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, ['pricingDetails', 'glBalance']);
};
export var getRetailPriceOfPrimaryVehicle = function getRetailPriceOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, ['pricingDetails', 'retailPrice']);
};
export var getVehicleIdOfPrimaryVehicle = function getVehicleIdOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, ['marketScanVehicleId']);
};
export var getSellingPriceOfPrimaryVehicle = function getSellingPriceOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, ['pricingDetails', 'sellingPrice']);
}; // do not return zero as default

export var getLoanSellingPriceOfPrimaryVehicle = function getLoanSellingPriceOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, ['pricingDetails', 'loanSellingPrice']);
};
export var getLoanSellingPriceBasedOnVehicleId = function getLoanSellingPriceBasedOnVehicleId(deal, vehicleId) {
  var vehicleDetails = getVehicleBasedOnId(deal, vehicleId);
  return _get(vehicleDetails, ['pricingDetails', 'loanSellingPrice']);
}; // do not return zero as default

export var getLeaseSellingPriceOfPrimaryVehicle = function getLeaseSellingPriceOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, ['pricingDetails', 'leaseSellingPrice']);
};
export var getLeaseSellingPriceBasedOnVehicleId = function getLeaseSellingPriceBasedOnVehicleId(deal, vehicleId) {
  var vehicleDetails = getVehicleBasedOnId(deal, vehicleId);
  return _get(vehicleDetails, ['pricingDetails', 'leaseSellingPrice']);
};
export var getMSRPOfPrimaryVehicle = function getMSRPOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, ['pricingDetails', 'msrp']);
};
export var getMRMOfPrimaryVehicle = function getMRMOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, ['pricingDetails', 'maxResidualizationMSRP']) || 0;
};
export var getCostAdjustmentsOfPrimaryVehicle = function getCostAdjustmentsOfPrimaryVehicle(deal) {
  var primaryVehicle = getPrimaryVehicle(deal);
  return _get(primaryVehicle, ['costAdjustments']);
};
export var getTradeIns = function getTradeIns(deal) {
  return _get(deal, 'tradeIns') || [];
};
export var getTradeInGross = function getTradeInGross(deal) {
  var tradeIns = _get(deal, 'tradeIns') || EMPTY_ARRAY;
  var totalSales = 0;
  var totalCost = 0;
  var totalDealerGross = 0;
  var breakUp = [];
  tradeIns.forEach(function (tradeIn) {
    var sales = tradeIn.tradeAllowance,
        cost = tradeIn.actualCashValue;
    var dealerGross = Money.subtract(cost, sales);
    totalSales = Money.add(totalSales, sales);
    totalCost = Money.add(totalCost, cost);
    totalDealerGross = Money.add(totalDealerGross, dealerGross);
    breakUp.push({
      sales: sales,
      cost: cost,
      dealerGross: dealerGross,
      tradeIn: tradeIn
    });
  });
  return {
    totalSales: totalSales,
    totalCost: totalCost,
    totalDealerGross: totalDealerGross,
    breakUp: breakUp
  };
};
export var getCostAdjustmentGross = function getCostAdjustmentGross(deal) {
  var grossType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : GROSS_TYPES.FRONT;
  var costAdjustments = getCostAdjustmentsOfPrimaryVehicle(deal) || EMPTY_ARRAY;
  var costAdjustmentOfType = costAdjustments.filter(function (costAdj) {
    return costAdj.grossType === grossType;
  });
  var totalSales = 0;
  var totalCost = 0;
  var totalDealerGross = 0;
  var breakUp = [];
  costAdjustmentOfType.forEach(function (costAdj) {
    var cost = costAdj.amount;
    var sales = 0; // NO sales amount;

    var dealerGross = Money.subtract(sales, cost);
    totalSales = Money.add(totalSales, sales);
    totalCost = Money.add(totalCost, cost);
    totalDealerGross = Money.add(totalDealerGross, dealerGross);
    breakUp.push({
      sales: sales,
      cost: cost,
      dealerGross: dealerGross,
      costAdj: costAdj
    });
  });
  return {
    totalSales: totalSales,
    totalCost: totalCost,
    totalDealerGross: totalDealerGross,
    breakUp: breakUp
  };
};
export var getCommissions = function getCommissions(deal) {
  return _get(deal, 'commissions') || EMPTY_ARRAY;
};
export var getFilteredCommissions = function getFilteredCommissions(deal) {
  return _filter(getCommissions(deal), 'userId');
};
export var getCommissionTotal = function getCommissionTotal(deal) {
  var commissions = _get(deal, 'commissions') || EMPTY_ARRAY;
  var amountTotal = commissions.reduce(function (acc, _ref11) {
    var amount = _ref11.amount;
    return Money.add(acc, amount);
  }, 0);
  var spiffTotal = commissions.reduce(function (acc, _ref12) {
    var spiffAmount = _ref12.spiffAmount;
    return Money.add(acc, spiffAmount);
  }, 0);
  return {
    spiffTotal: spiffTotal,
    amountTotal: amountTotal,
    totalCommission: Money.add(spiffTotal, amountTotal)
  };
};
export var shouldCollectSalesTaxUpfront = function shouldCollectSalesTaxUpfront(deal, type, taxAndFeeMetaData) {
  var marketId = getMarketId(deal, type);
  var taxOptions = ZipCodeTaxReader.getTaxOptionsForState(taxAndFeeMetaData, marketId);

  var salesTaxInCapOption = _find(taxOptions, function (option) {
    return option.key === 'salesTaxInCap';
  }); // TAX_FEE_OPTIONS


  var valueForsalesTaxInCapOptionFlag = getValueOfStateFeeTaxOption(deal, 'salesTaxInCap', type);
  return !_isEmpty(salesTaxInCapOption) && !valueForsalesTaxInCapOptionFlag;
};
export var isDealStatusGreaterThanStatus = function isDealStatusGreaterThanStatus(deal, requiredStatus) {
  var dealStatus = getDealStatus(deal);
  var currentDealStatusWeight = DEAL_STATUS_WEIGHTS[dealStatus];
  var weightOfRequiredStatus = DEAL_STATUS_WEIGHTS[requiredStatus];
  return currentDealStatusWeight > weightOfRequiredStatus;
};
export var addUserToCommisions = function addUserToCommisions(commissions, persona, userId) {
  var newCommissionObj = {
    userType: persona,
    userId: userId
  };
  var indexOfPersona = commissions.findIndex(function (_ref13) {
    var userType = _ref13.userType;
    return userType === persona;
  });
  if (indexOfPersona < 0) return (commissions || []).concat([newCommissionObj]);

  var newCommissions = _toConsumableArray(commissions);

  _set(newCommissions, [indexOfPersona], newCommissionObj);

  return newCommissions;
};
export var getTotalTradePayoff = function getTotalTradePayoff(deal) {
  var tradeIns = _get(deal, 'tradeIns');

  if (tradeIns) {
    return tradeIns.reduce(function (tradePayoff, item) {
      return Money.add(tradePayoff, _get(item, 'tradePayOff', 0));
    }, 0);
  }

  return 0;
};
export var getDmvRosNumber = function getDmvRosNumber(deal) {
  return _get(deal, 'dmvRosNumber');
};
export var getTotalTradeAllowance = function getTotalTradeAllowance(deal) {
  var tradeIns = _get(deal, 'tradeIns');

  if (tradeIns) {
    return tradeIns.reduce(function (tradeAllowance, item) {
      return Money.add(tradeAllowance, _get(item, 'tradeAllowance', 0));
    }, 0);
  }

  return 0;
};
export var getTotalRetainedEquity = function getTotalRetainedEquity(deal) {
  var tradeIns = _get(deal, 'tradeIns');

  if (tradeIns) {
    return tradeIns.reduce(function (retainedEquity, item) {
      return Money.add(retainedEquity, _get(item, 'retainedEquity', 0));
    }, 0);
  }

  return 0;
};
export var getNetTradeInValue = function getNetTradeInValue(deal) {
  var tradeIns = _get(deal, 'tradeIns');

  if (tradeIns) {
    return tradeIns.reduce(function (netTradeIn, item) {
      return Money.add(netTradeIn, Money.subtract(getNumber(item.tradeAllowance), Money.add(getNumber(item.tradePayOff), getNumber(item.retainedEquity))));
    }, 0);
  }

  return 0;
};
export var getContratInTransit = function getContratInTransit() {
  var deal = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var contract = deal.contract,
      _deal$dealVehiclePaym = deal.dealVehiclePaymentInfos,
      dealVehiclePaymentInfos = _deal$dealVehiclePaym === void 0 ? EMPTY_ARRAY : _deal$dealVehiclePaym;

  var _ref14 = (_get(contract, 'dealFollowUpForPayment') || EMPTY_ARRAY).find(function (_ref15) {
    var type = _ref15.type;
    return type === RECEIVABLES_TYPES.CONTARCTS_IN_TRANSIT;
  }) || EMPTY_OBJECT,
      pendingAmt = _ref14.pendingAmt;

  var getAmountFinanced = function getAmountFinanced() {
    var paymentInfo = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
    return _get(paymentInfo, [[0], 'dealPayments', [0], 'termPaymentDetails', [0], 'amountFinanced']) || '';
  };

  return !_isEmpty(contract) ? pendingAmt : getAmountFinanced(dealVehiclePaymentInfos);
};
export var getPrimaryVehicleType = function getPrimaryVehicleType(deal) {
  var primaryVehicle = getPrimaryVehicle(deal) || EMPTY_OBJECT;
  return primaryVehicle.vehicleType || '';
};
export var getStateFeeTaxOptions = function getStateFeeTaxOptions(deal, type) {
  return _get(deal, ['stateFeeTaxOptions', type]) || EMPTY_OBJECT;
};

var getValueOfStateFeeTaxOption = function getValueOfStateFeeTaxOption(deal, optionName, type) {
  return _get(getStateFeeTaxOptions(deal, type), [optionName]);
};

export var getVehicleAge = function getVehicleAge(deal, primaryVehicle) {
  return Math.max(0, Math.floor(getVehicleAgeInDays(primaryVehicle)));
};
export var getFNIBackGross = function getFNIBackGross(deal) {
  return getNumber(_get(deal, 'grossDetails.backFnIAmt'));
};
export var getFNIFrontGross = function getFNIFrontGross(deal) {
  return getNumber(_get(deal, 'grossDetails.frontFnIAmt'));
};
export var getAcquisitionFeeMarkup = function getAcquisitionFeeMarkup(deal) {
  return getNumber(_get(deal, 'grossDetails.acquisitionFeeMarkupAmt'));
};
export var getAccessoriesBackGross = function getAccessoriesBackGross(deal) {
  return getNumber(_get(deal, 'grossDetails.backAccAmt'));
};
export var getCommissionableGross = function getCommissionableGross(deal) {
  return getNumber(_get(deal, 'grossDetails.commissionableGrossAmt'));
};
export var getPenEnabled = function getPenEnabled(deal) {
  return _get(deal, 'penEnabled') || false;
}; // eslint-disable-next-line no-shadow

export function getTaxRateFromDeal(deal, isLoanOrCashOrBalloonPaymentType) {
  var taxAndZipCodeDetails = getTaxAndZipCodeDetails(deal, isLoanOrCashOrBalloonPaymentType ? TAX_AND_ZIP_CODE_DETAILS_VS_KEY.RETAIL : TAX_AND_ZIP_CODE_DETAILS_VS_KEY.LEASE);
  var loanTax = ZipCodeTaxReader.getLoanTax(taxAndZipCodeDetails);
  var leaseTax = ZipCodeTaxReader.getLeaseTax(taxAndZipCodeDetails);

  var loanTaxRate = _get(loanTax, 'taxRate');

  var leaseTaxRate = _get(leaseTax, 'taxRate');

  if (loanTaxRate && isLoanOrCashOrBalloonPaymentType) {
    return loanTaxRate;
  }

  if (leaseTaxRate) {
    return leaseTaxRate;
  }

  return _get(taxAndZipCodeDetails, 'taxRate') || 0;
} // eslint-disable-next-line no-shadow

export function getTaxRateForFNIDeal(deal, isLoanOrCashOrBalloonPaymentType) {
  var taxAndZipCodeDetails = getTaxAndZipCodeDetails(deal, isLoanOrCashOrBalloonPaymentType ? TAX_AND_ZIP_CODE_DETAILS_VS_KEY.RETAIL : TAX_AND_ZIP_CODE_DETAILS_VS_KEY.LEASE);
  var loanTax = ZipCodeTaxReader.getLoanTax(taxAndZipCodeDetails);
  var leaseTax = ZipCodeTaxReader.getLeaseTax(taxAndZipCodeDetails);

  var loanTaxRate = _get(loanTax, 'taxRate');

  var warrantyTaxRate = _get(loanTax, 'WarrantyTaxPct');

  var leaseTaxRate = _get(leaseTax, 'taxRate');

  if (warrantyTaxRate) {
    return warrantyTaxRate;
  }

  if (loanTaxRate && isLoanOrCashOrBalloonPaymentType) {
    return loanTaxRate;
  }

  if (leaseTaxRate) {
    return leaseTaxRate;
  }

  return _get(taxAndZipCodeDetails, 'taxRate') || 0;
}
export var getUserId = function getUserId(deal) {
  return _get(deal, 'userId') || EMPTY_STRING;
};
export var getPrimarySalesPersonId = function getPrimarySalesPersonId(deal) {
  return _get(deal, 'assignee.salesPersons[0]') || EMPTY_STRING;
};
export var getSalesManagerId = function getSalesManagerId(deal) {
  return _get(deal, 'assignee.salesManager[0]') || EMPTY_STRING;
};
export var getFinanceManagerId = function getFinanceManagerId(deal) {
  return _get(deal, 'assignee.financeManager[0]') || EMPTY_STRING;
};
export var getAllUsersOfDeal = function getAllUsersOfDeal(deal) {
  var assignee = _get(deal, 'assignee');

  return _compact(_reduce(assignee, function (allUsers, assigneeListByPersona) {
    return Array.isArray(assigneeListByPersona) ? [].concat(_toConsumableArray(allUsers), _toConsumableArray(assigneeListByPersona)) : allUsers;
  }, []));
};
export function getPenInfo(deal) {
  return _get(deal, 'penInfo') || EMPTY_OBJECT;
}
export var getEmployerCategoryOfBuyer = function getEmployerCategoryOfBuyer(deal) {
  var buyer = getBuyer(deal);
  var currentEmploymentInfo = getCurrentEmploymentInfoOfBuyer(buyer);
  return _get(currentEmploymentInfo, 'employerType');
};
export var showConcierge = function showConcierge(deal) {
  return _get(deal, 'conciergeActivated') || false;
};
export var getAdjustmentAmountByType = function getAdjustmentAmountByType(deal, type) {
  var costAdjustments = getCostAdjustmentsOfPrimaryVehicle(deal) || [];
  var roAdjustment = costAdjustments.find(function (_ref16) {
    var description = _ref16.description,
        userId = _ref16.userId;
    return description === type && isCreatedBySystem(userId);
  }) || {};
  return getNumber(_get(roAdjustment, 'amount'));
};
export var getWorkingCashPreset = function getWorkingCashPreset(deal) {
  return _get(deal, 'leaseConfigurations.workingCashPresets');
};
export var getDisplayBaseMonthlyPayment = function getDisplayBaseMonthlyPayment(deal) {
  return _get(deal, 'leaseConfigurations.displayBaseMonthlyPayment');
};
export var getSecurityDepositWaiverReason = function getSecurityDepositWaiverReason(deal) {
  return _get(deal, 'securityDepositWaiverReason');
};
export function getSalesPersons(deal) {
  return _get(deal, 'assignee.salesPersons') || EMPTY_ARRAY;
}
export var collectTaxes = function collectTaxes(deal, type) {
  if (type === TAX_AND_ZIP_CODE_DETAILS_VS_KEY.LEASE) return _get(deal, 'collectTaxLease');
  return _get(deal, 'collectTaxRetail');
};
export var collectFees = _property('collectFees');
export var getTradeInVehicleFromVehicleDetails = function getTradeInVehicleFromVehicleDetails(vehicleDetails) {
  var trimDetails = _get(vehicleDetails, 'trimDetails');

  var trim = _get(vehicleDetails, 'trim') || EMPTY_STRING;

  if (!_isEmpty(trimDetails) && !trim) {
    trim = getTrimValuesWithSpaceSeparator(trimDetails);
  }

  return {
    exteriorColor: _get(vehicleDetails, 'exteriorColor'),
    interionColor: _get(vehicleDetails, 'interionColor'),
    make: _get(vehicleDetails, 'make'),
    mileage: _get(vehicleDetails, 'mileage'),
    model: _get(vehicleDetails, 'model'),
    trim: trim,
    trimDetails: trimDetails,
    vin: _get(vehicleDetails, 'vin'),
    vinLookupResolved: _get(vehicleDetails, 'vinLookupResolved'),
    year: _get(vehicleDetails, 'year')
  };
};
export var getTradeInDataForRefinanceDeal = function getTradeInDataForRefinanceDeal() {
  var vehicles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  return _map(vehicles, function (vehicleData) {
    var tradeInVehicle = getTradeInVehicleFromVehicleDetails(vehicleData);
    return {
      division: EMPTY_STRING,
      isTradeInDirty: true,
      newlyAdded: true,
      ownerShipType: EMPTY_STRING,
      ownerType: EMPTY_STRING,
      tradeInVehicle: tradeInVehicle
    };
  });
};
export var getRebateProviderType = function getRebateProviderType(deal, dealVehicleId) {
  var vehicleIdToUse = dealVehicleId || getPrimaryVehicleDealVehicleId(deal);
  var dealVehiclePaymentInfo = _get(deal, 'dealVehiclePaymentInfos') || [];
  return _get(_find(dealVehiclePaymentInfo, function (_ref17) {
    var vehicleId = _ref17.dealVehicleId;
    return vehicleId === vehicleIdToUse;
  }), 'rebateProvider');
};
export var getDeposit = function getDeposit(deal) {
  return _get(deal, 'depositDetails.deposit');
};
export var getDepositDetails = function getDepositDetails(deal) {
  return _get(deal, 'depositDetails') || EMPTY_OBJECT;
};
export var getPrimaryVehicleStatus = function getPrimaryVehicleStatus(deal) {
  var primaryVehicle = getPrimaryVehicle(deal) || EMPTY_OBJECT;
  return primaryVehicle.status;
};
export var getEnableSeparateTaxCalc = function getEnableSeparateTaxCalc(deal) {
  return _get(deal, 'separateZipForRetailAndLease');
};
export var getLeaseWorkingCashConfigs = function getLeaseWorkingCashConfigs(deal) {
  return _get(deal, 'workingCashConfig.leaseWorkingCashConfigs');
};
export var getDeliveryCode = function getDeliveryCode(deal, dealVehicleId) {
  var vehicleIdToUse = dealVehicleId || getPrimaryVehicleDealVehicleId(deal);
  var dealVehiclePaymentInfo = _get(deal, 'dealVehiclePaymentInfos') || [];
  return _get(_find(dealVehiclePaymentInfo, function (_ref18) {
    var vehicleId = _ref18.dealVehicleId;
    return vehicleId === vehicleIdToUse;
  }), 'deliveryCode');
};
export var getDealCreatedAtSite = function getDealCreatedAtSite(deal) {
  return _get(deal, 'soldAtSiteId');
};
export var isPreOrderDeal = function isPreOrderDeal(deal) {
  return getDealStatus(deal) === DEAL_STATUS.PREORDER;
};
export var isPreClosedDeal = function isPreClosedDeal(deal) {
  return getDealStatus(deal) === DEAL_STATUS.PRE_CLOSED;
};
export var isMultiVehicleDeskingEnabledV2 = function isMultiVehicleDeskingEnabledV2(deal) {
  return !!_get(deal, 'multiVehicleDeskingEnabled');
};
export var getAllDealVehicleIds = function getAllDealVehicleIds(deal) {
  var vehicles = _get(deal, 'vehicles') || EMPTY_ARRAY;
  return _map(vehicles, 'dealVehicleId');
};
export var getDealLookupUsers = function getDealLookupUsers(deal) {
  return _get(deal, '_lookup.DSE_USER') || EMPTY_OBJECT;
};
export var getLeadId = function getLeadId(deal) {
  return _get(deal, 'leadId') || EMPTY_STRING;
};
export var getRefreshPayments = function getRefreshPayments(deal) {
  return _get(deal, 'refreshMScanPayments') || false;
};
export var getTekionInternalProductInfo = function getTekionInternalProductInfo(deal) {
  return _get(deal, 'tekionInternalProductInfo') || EMPTY_OBJECT;
};
export var getTekionInternalProductContractInfo = function getTekionInternalProductContractInfo(deal) {
  return _get(getTekionInternalProductInfo(deal), 'productContractInfos') || EMPTY_ARRAY;
};
export var getOverrideFees = function getOverrideFees(deal) {
  return !!(deal !== null && deal !== void 0 && deal.overrideFees);
};
export var getDealSource = function getDealSource(deal) {
  return _get(deal, 'source', EMPTY_STRING);
};